import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { fromEvent, merge, from, of } from 'rxjs';
import { debounceTime as rxDebounceTime, distinctUntilChanged, tap, filter, switchMap, pluck, catchError } from 'rxjs/operators';
import { isBackspace } from 'spark-util/keyboardEvents';
import stateReducer from './multiselectReducer';
import { renderSelectItems } from './SelectItem';
import { SearchIcon } from './SelectInput';
import SelectedItemList from './SelectedItemList';
import SelectBase from './SelectBase';
import { ClearIcon } from './SelectInput';

const DEFAULT_DEBOUNCE_MS = 150;

const atLeastMinSearchLength = (minLength, text) => text && text.length >= minLength;

const AsyncWrapper = (props) => {
    const {
        label,
        placeholder,
        options: optionsProp,
        onChange,
        onBlur,
        minSearchLength = 2,
        maxLength,
        debounceTime = DEFAULT_DEBOUNCE_MS,
        multiple,
        initialValue,
        disabled
    } = props;

    const searchRef = useRef(null);
    const optionsLoaderRef = useRef(optionsProp);
    optionsLoaderRef.current = optionsProp;
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);

    const reducer = useMemo(() => multiple ? stateReducer(true) : undefined, [multiple]);
    const disabledValue = props.disabledValue;
    const renderOption = useMemo(() => renderSelectItems(
        multiple
            ? () => false
            : (item, selected) => selected && item.value === selected.value
    ), [multiple]);

    const searchStarted = useCallback(() => setSearching(true), []);
    const searchFinished = useCallback(() => setSearching(false), []);
    const setAvailableOptions = useCallback((opts) => setOptions(opts), []);
    const clearOptions = useCallback(() => setOptions([]), []);
    const clearInputValue = useCallback(() => setOptions([]), []);

    useEffect(() => {
        if (!searchRef.current) return;

        const searchText = merge(
            fromEvent(searchRef.current, 'keyup'),
            fromEvent(searchRef.current, 'keydown').pipe(filter(isBackspace))
        )
            .pipe(
                pluck('target', 'value'),
                distinctUntilChanged(),
                tap(clearOptions),
                tap(searchStarted),
                rxDebounceTime(debounceTime),
                filter(search => atLeastMinSearchLength(minSearchLength, search)),
                switchMap(searchText =>
                    from(optionsLoaderRef.current(searchText)).pipe(catchError(() => of([])))
                ),
                tap(searchFinished)
            )
            .subscribe(setAvailableOptions);

        return () => {
            searchText.unsubscribe();
            setSearching(false);
        };
    }, [minSearchLength, debounceTime, clearOptions, searchStarted, searchFinished, setAvailableOptions]);

    const filterOptions = useCallback(({ selectedItem }) => item => {
        return !selectedItem.some(selected => selected.value === item.value);
    }, []);

    const onBlurHandler = useCallback(event => {
        onBlur && onBlur(event);
    }, [onBlur]);

    const helpText = useCallback(({ inputValue }) =>
        atLeastMinSearchLength(minSearchLength, inputValue)
            ? searching
                ? 'Searching...'
                : 'No results found'
            : 'Start typing to search...',
        [minSearchLength, searching]
    );

    const renderSearchIcon = useCallback((disabled) => () => <SearchIcon disabled={disabled} />, []);
    const renderInputDecoration = useCallback(disabled => ({ selectedItem, clearSelection }) =>
        !selectedItem || disabled ? (
            <SearchIcon />
        ) : (
            <span onClick={clearSelection}>
                <ClearIcon />
            </span>
        ),
        []
    );

    const blankStr = () => '';
    const selectedValueText = item => (item ? item.label : '');

    const value = initialValue
        ? initialValue
        : multiple
            ? []
            : '';

    return (
        <SelectBase
            label={label}
            placeholder={placeholder}
            options={options}
            initialValue={value}
            stateReducer={reducer}
            noResultsText={helpText}
            inputRef={searchRef}
            maxLength={maxLength}
            onChange={onChange}
            onSelect={clearOptions}
            onBlur={onBlurHandler}
            onInputClick={clearInputValue}
            disabledValueReport={disabledValue}
            disabled={disabled}
            selectedItemToString={multiple ? blankStr : selectedValueText}
            filterOptions={multiple && filterOptions}
            renderOption={renderOption}
            renderSelected={multiple && SelectedItemList}
            renderInputDecoration={
                multiple
                    ? renderSearchIcon(disabled)
                    : renderInputDecoration(disabled)
            }
        />
    );
};

AsyncWrapper.propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    minSearchLength: PropTypes.number,
    maxLength: PropTypes.number,
    debounceTime: PropTypes.number,
    multiple: PropTypes.bool,
    initialValue: PropTypes.any,
    disabled: PropTypes.bool,
    disabledValue: PropTypes.any,
};

export default AsyncWrapper;
